import { DragEvent, HTMLAttributes, useState } from "react";

import type { DropPosition } from "./useDragReorder";

type Drag = { type: "category"; index: number } | { type: "section"; categoryUid: string; index: number };

// "into" = drop a section onto a category header or empty category, appending it.
type Drop =
  | { type: "category"; index: number; position: DropPosition }
  | { type: "section"; categoryUid: string; index: number; position: DropPosition | "into" };

const half = (e: DragEvent<HTMLElement>): DropPosition => {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientY < rect.top + rect.height / 2 ? "before" : "after";
};

/**
 * Two-level drag for the template sidebar. Categories only land among categories and sections
 * only land inside a category; a mismatched hover never calls preventDefault, so it can't drop.
 */
export function useSidebarDrag({
  onReorderCategory,
  onMoveSection,
  sectionCount,
}: {
  onReorderCategory: (from: number, to: number) => void;
  onMoveSection: (fromCategoryUid: string, fromIndex: number, toCategoryUid: string, toIndex: number) => void;
  sectionCount: (categoryUid: string) => number;
}) {
  const [drag, setDrag] = useState<Drag | null>(null);
  const [drop, setDrop] = useState<Drop | null>(null);

  const end = () => {
    setDrag(null);
    setDrop(null);
  };

  const start = (next: Drag) => (e: DragEvent<HTMLElement>) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
    setDrag(next);
  };

  const commit = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (drag?.type === "category" && drop?.type === "category") {
      let to = drop.position === "after" ? drop.index + 1 : drop.index;
      if (drag.index < to) to -= 1;
      onReorderCategory(drag.index, to);
    }
    if (drag?.type === "section" && drop?.type === "section") {
      let to =
        drop.position === "into"
          ? sectionCount(drop.categoryUid)
          : drop.position === "after"
            ? drop.index + 1
            : drop.index;
      const sameList = drag.categoryUid === drop.categoryUid;
      if (sameList && drag.index < to) to -= 1;
      onMoveSection(drag.categoryUid, drag.index, drop.categoryUid, to);
    }
    end();
  };

  /** Category header row: reorders categories, and accepts a section dropped "into" it. */
  const categoryHandlers = (categoryUid: string, index: number): HTMLAttributes<HTMLElement> => ({
    draggable: true,
    onDragStart: start({ type: "category", index }),
    onDragOver: (e) => {
      if (!drag) return;
      e.preventDefault();
      e.stopPropagation();
      setDrop(
        drag.type === "category"
          ? { type: "category", index, position: half(e) }
          : { type: "section", categoryUid, index: 0, position: "into" }
      );
    },
    onDrop: commit,
    onDragEnd: end,
  });

  const sectionHandlers = (categoryUid: string, index: number): HTMLAttributes<HTMLElement> => ({
    draggable: true,
    onDragStart: start({ type: "section", categoryUid, index }),
    onDragOver: (e) => {
      if (drag?.type !== "section") return;
      e.preventDefault();
      e.stopPropagation();
      setDrop({ type: "section", categoryUid, index, position: half(e) });
    },
    onDrop: commit,
    onDragEnd: end,
  });

  /** Placeholder shown in an empty category so a section has somewhere to land. */
  const emptyHandlers = (categoryUid: string): HTMLAttributes<HTMLElement> => ({
    onDragOver: (e) => {
      if (drag?.type !== "section") return;
      e.preventDefault();
      e.stopPropagation();
      setDrop({ type: "section", categoryUid, index: 0, position: "into" });
    },
    onDrop: commit,
  });

  const isDraggingSection = (categoryUid: string, index: number) =>
    drag?.type === "section" && drag.categoryUid === categoryUid && drag.index === index;

  const categoryDrop = (categoryUid: string, index: number): DropPosition | "into" | null => {
    if (drop?.type === "category" && drop.index === index && drag?.type === "category" && drag.index !== index) {
      return drop.position;
    }
    if (drop?.type === "section" && drop.position === "into" && drop.categoryUid === categoryUid) return "into";
    return null;
  };

  const sectionDrop = (categoryUid: string, index: number): DropPosition | null => {
    if (drop?.type !== "section" || drop.position === "into") return null;
    if (drop.categoryUid !== categoryUid || drop.index !== index) return null;
    return isDraggingSection(categoryUid, index) ? null : drop.position;
  };

  return {
    dragType: drag?.type ?? null,
    categoryHandlers,
    sectionHandlers,
    emptyHandlers,
    categoryDrop,
    sectionDrop,
    isDraggingSection,
  };
}
